import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Switch,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  CheckCircleOutline,
  CloudOffOutlined,
  ArchiveOutlined,
  CloudUploadOutlined,
  UnarchiveOutlined,
  ErrorOutline,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  HistoryOutlined,
  PublicOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import { fetchData } from '../../../helpers/apiUtils';
import { HubBroker, HubSite } from '../types';
import NewSnippetDialog from './NewSnippetDialog';
import SnippetAusspielenDialog from './SnippetAusspielenDialog';
import SnippetEditorDialog from './SnippetEditorDialog';
import {
  SNIPPET_GLOBAL_SITE_ID,
  SnippetDeployResult,
  SnippetListRow,
  SnippetOwnership,
  SnippetSiteState,
  SnippetVersion,
} from './types';

interface Props {
  sites: HubSite[];
  brokers: HubBroker[];
}

/**
 * Schnipsel-Übersicht: was es gibt, auf welcher EBENE es liegt, und wo es ausgespielt ist.
 *
 * ⚠ Die Ebene ist hier kein Detail, sondern die Hauptspalte. Wer die globale Fassung bearbeitet,
 * während ein Portal eine eigene hat, ändert dort nichts und merkt es nicht — das ist die Form des
 * Vorfalls vom 21.08. Die aufgeklappte Zeile zeigt deshalb je Portal, WER den Namen dort gerade
 * besitzt (`/snippets/ownership/:slug`, dieselbe Auflösung wie beim Ausspielen), nicht nur den
 * eigenen Ausspielstand.
 */
/**
 * Aus den Ergebniszeilen einen Satz machen, der sagt, WAS JETZT AUF DEN PORTALEN STEHT.
 *
 * ============================================================================================
 * WARUM NICHT EINFACH ZAEHLEN
 * ============================================================================================
 * „Zurückgenommen: 6 von 6 Portalen" stand bei der Abnahme 2026-08-25 auf dem Schirm, während auf
 * Trading-Treff in Wahrheit die GLOBALE Fassung ausgespielt wurde — entfernt worden war dort
 * nichts (A40-B2). Formal war der Satz nicht falsch; als Auskunft war er unbrauchbar.
 *
 * Deshalb wird nach WIRKUNG gruppiert und nicht nach `ok`. Und der Sonderfall, der den Befund
 * ausgeloest hat, bekommt einen eigenen Satz: ein Portal, auf dem nach der Aktion eine ANDERE
 * Fassung gilt als die, auf der der Knopf sass.
 *
 * ⚠ Der Nenner ist `ergebnisse.length` — seit der Dienst je angefragtem Portal genau eine Zeile
 * liefert, ist das wirklich „von den angefragten Portalen". Vorher fehlte die Zeile fuer „nichts
 * zu tun" ganz, und die Zahl bedeutete etwas anderes, als sie las.
 */
export const meldungAus = (
  ergebnisse: SnippetDeployResult[],
  was: 'deploy' | 'takedown',
  row: SnippetListRow,
): { art: 'success' | 'error'; text: string } => {
  const namen = (liste: SnippetDeployResult[]) => liste.map((e) => e.site_key).join(', ');
  const gescheitert = ergebnisse.filter((e) => e.wirkung === 'fehlgeschlagen');
  const uebersprungen = ergebnisse.filter((e) => e.wirkung === 'uebersprungen');
  const geraeumt = ergebnisse.filter((e) => e.wirkung === 'geraeumt');
  const unveraendert = ergebnisse.filter((e) => e.wirkung === 'unveraendert');
  const gespeichert = ergebnisse.filter((e) => e.wirkung === 'gespeichert');
  // ⚠ DER KERN DES BEFUNDS: dort gilt jetzt eine andere Fassung als die bediente.
  const zurueckgefallen = gespeichert.filter((e) => Number(e.owner_id) !== Number(row.id));
  const eigene = gespeichert.filter((e) => Number(e.owner_id) === Number(row.id));

  const teile: string[] = [];
  if (eigene.length) {
    teile.push(
      `${eigene.length} Portal(e) tragen jetzt diese Fassung (${namen(eigene)})`,
    );
  }
  if (zurueckgefallen.length) {
    teile.push(
      `${namen(zurueckgefallen)} ${zurueckgefallen.length === 1 ? 'trägt' : 'tragen'} jetzt eine ANDERE Fassung dieses Namens — dort wurde nichts entfernt`,
    );
  }
  if (geraeumt.length) teile.push(`geräumt: ${namen(geraeumt)}`);
  // ⚠ „unverändert" deckt ZWEI Faelle ab, und beide heissen fuer den Bedienenden dasselbe: dort
  // galt schon nichts, ODER dort lag schon genau dieser Stand. Die frueher hier stehende Klammer
  // „(dort galt schon nichts)" war seit dem Prod-Lauf am 2026-08-25 nur noch fuer die Haelfte wahr.
  if (unveraendert.length) teile.push(`unverändert: ${namen(unveraendert)}`);
  if (uebersprungen.length) teile.push(`ohne Empfänger: ${namen(uebersprungen)}`);
  if (gescheitert.length) {
    teile.push(
      `offen: ${gescheitert.map((e) => `${e.site_key} (${e.error ?? 'ohne Grund'})`).join(', ')}`,
    );
  }

  return {
    art: gescheitert.length ? 'error' : 'success',
    text:
      `${was === 'deploy' ? 'Ausgespielt' : 'Zurückgenommen'} auf ${ergebnisse.length} Portal(en) — ` +
      (teile.length ? `${teile.join(' · ')}.` : 'nichts zu tun.'),
  };
};

const SnippetsTab: React.FC<Props> = ({ sites, brokers }) => {
  const [rows, setRows] = useState<SnippetListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [bearbeiten, setBearbeiten] = useState<number | null>(null);
  const [offen, setOffen] = useState<number | null>(null);
  const [detail, setDetail] = useState<{
    sites: SnippetSiteState[];
    ownership: SnippetOwnership[];
  } | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [aktion, setAktion] = useState<number | null>(null);
  const [meldung, setMeldung] = useState<{ art: 'success' | 'error'; text: string } | null>(null);
  const [historie, setHistorie] = useState<{ row: SnippetListRow; v: SnippetVersion[] } | null>(
    null,
  );
  /** Der NAME, fuer den der Bestaetigen-Dialog offen ist — die Aktion gilt dem Namen (A40-B1). */
  const [ausspielenFuer, setAusspielenFuer] = useState<string | null>(null);
  /** Archivierte einblenden. Standard aus — sie sind aus der ARBEITSLISTE geraeumt (A40-B3). */
  const [zeigeArchivierte, setZeigeArchivierte] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    const r = await fetchData<SnippetListRow[]>(
      `/api/broker-hub/snippets${zeigeArchivierte ? '?archiviert=1' : ''}`,
    );
    setLoading(false);
    if (!r.success) {
      setError(r.error || 'Die Schnipsel konnten nicht geladen werden.');
      return;
    }
    setError(null);
    setRows(r.data ?? []);
  }, [zeigeArchivierte]);

  useEffect(() => {
    laden();
  }, [laden]);

  // Details einer Zeile: eigener Ausspielstand UND die Besitzverhältnisse des Namens.
  const detailsLaden = useCallback(async (row: SnippetListRow) => {
    setDetailBusy(true);
    setDetail(null);
    const [st, own] = await Promise.all([
      fetchData<SnippetSiteState[]>(`/api/broker-hub/snippets/${row.id}/sites`),
      fetchData<SnippetOwnership[]>(
        `/api/broker-hub/snippets/ownership/${encodeURIComponent(row.slug)}`,
      ),
    ]);
    setDetailBusy(false);
    setDetail({
      sites: st.success && st.data ? st.data : [],
      ownership: own.success && own.data ? own.data : [],
    });
  }, []);

  const umschalten = (row: SnippetListRow) => {
    if (offen === row.id) {
      setOffen(null);
      setDetail(null);
      return;
    }
    setOffen(row.id);
    detailsLaden(row);
  };

  /**
   * Ausspielen bzw. Zurücknehmen. Beide antworten 200 auch bei Teilerfolg — fail-soft je Portal
   * ist die Zusage, also wird hier gezählt statt „erfolgreich"/„fehlgeschlagen" behauptet.
   */
  const ausspielen = async (
    row: SnippetListRow,
    was: 'deploy' | 'takedown',
    zielPortale?: number[],
  ) => {
    setAktion(row.id);
    setMeldung(null);
    // ⚠ Der Filter geht NUR beim Ausspielen mit. Eine Ruecknahme schaltet die FASSUNG ab, und
    // welche Portale das trifft, weiss die Oberflaeche nicht — siehe `takedownSnippet`.
    const rumpf = was === 'deploy' && zielPortale?.length ? { zielPortale } : {};
    const r = await fetchData<SnippetDeployResult[]>(
      `/api/broker-hub/snippets/${row.id}/${was}`,
      { method: 'POST', body: JSON.stringify(rumpf) },
    );
    setAktion(null);
    const ergebnisse = r.data ?? [];
    if (!r.success && ergebnisse.length === 0) {
      setMeldung({ art: 'error', text: r.error || 'Der Vorgang ist fehlgeschlagen.' });
    } else {
      setMeldung(meldungAus(ergebnisse, was, row));
    }
    await laden();
    if (offen === row.id) await detailsLaden(row);
  };

  /**
   * Archivieren bzw. zurueckholen.
   *
   * ⚠ Es gibt bewusst KEIN Loeschen. Die Zeile bleibt samt Historie und Ausspielzustand — die
   * Begruendung steht im Kopf der Migration. „Archiviert" heisst „aus der Arbeitsliste geraeumt".
   */
  const archivieren = async (row: SnippetListRow, zurueck = false) => {
    setAktion(row.id);
    setMeldung(null);
    const r = await fetchData<unknown>(
      `/api/broker-hub/snippets/${row.id}/${zurueck ? 'unarchive' : 'archive'}`,
      { method: 'POST', body: '{}' },
    );
    setAktion(null);
    setMeldung(
      r.success
        ? {
            art: 'success',
            text: zurueck
              ? `${row.slug} steht wieder in der Liste.`
              : `${row.slug} ist archiviert — die Fassung und ihre Historie bleiben erhalten.`,
          }
        : { art: 'error', text: r.error || 'Der Vorgang ist fehlgeschlagen.' },
    );
    await laden();
  };

  const historieOeffnen = async (row: SnippetListRow) => {
    const r = await fetchData<SnippetVersion[]>(`/api/broker-hub/snippets/${row.id}/versions`);
    setHistorie({ row, v: r.success && r.data ? r.data : [] });
  };

  const gruppiert = useMemo(() => {
    const map = new Map<string, SnippetListRow[]>();
    for (const r of rows) {
      const liste = map.get(r.slug) ?? [];
      liste.push(r);
      map.set(r.slug, liste);
    }
    return map;
  }, [rows]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/*
        ⚠ `flexWrap` ist nicht Kosmetik: ohne ihn schiebt die Zeile die Chips bei schmalen
        Fenstern aus dem Bild und die SEITE bekommt einen waagerechten Scrollbalken (gemessen bei
        500px: 525 statt 500). Die Tabelle darunter scrollt korrekt in ihrem eigenen Container —
        der Seitenkoerper darf es nie.
      */}
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={() => setNeuOffen(true)}
        >
          Neuer Schnipsel
        </Button>
        <Box flexGrow={1} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={zeigeArchivierte}
              onChange={(e) => setZeigeArchivierte(e.target.checked)}
            />
          }
          label="Archivierte zeigen"
        />
        <Chip label={`${gruppiert.size} Namen`} variant="outlined" />
        <Chip label={`${rows.length} Fassungen`} variant="outlined" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {meldung && (
        <Alert severity={meldung.art} sx={{ mb: 2 }} onClose={() => setMeldung(null)}>
          {meldung.text}
        </Alert>
      )}

      {rows.length === 0 && !error && (
        <Alert severity="info">
          Es gibt noch keine Schnipsel. Ein Schnipsel ist ein wiederverwendbarer Baustein-Block, den
          ein Artikel per <code>{'[bh name="…"]'}</code> einbindet.
        </Alert>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Name</TableCell>
                <TableCell>Ebene</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell align="center">Ausgespielt</TableCell>
                <TableCell>Zuletzt</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...gruppiert.entries()].flatMap(([slug, fassungen]) => [
                /*
                  ⚠ NAMENSZEILE — hier sitzt das Ausspielen.
                  Es galt schon immer dem ganzen Namen (`syncSnippet(slug)`), sah in der Zeile
                  einer Fassung aber aus wie „diese eine". Bei der Abnahme 2026-08-25 schrieb ein
                  Klick auf der Trading-Treff-Zeile auf alle sechs Portale (A40-B1).

                  ⚠ Die RUECKNAHME bleibt unten je Fassung: sie schaltet genau eine ab, und eine
                  „Gruppen-Ruecknahme" haette keine definierte Bedeutung (welche Fassung?).
                */
                <TableRow key={`kopf-${slug}`} sx={{ bgcolor: 'action.hover' }}>
                  <TableCell />
                  <TableCell colSpan={5}>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                      {slug}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fassungen.length === 1 ? 'eine Fassung' : `${fassungen.length} Fassungen`}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ausspielen — gilt dem ganzen Namen; im naechsten Schritt laesst sich je Portal abwaehlen">
                      <span>
                        <Button
                          size="small"
                          startIcon={<CloudUploadOutlined fontSize="small" />}
                          disabled={aktion !== null}
                          onClick={() => setAusspielenFuer(slug)}
                        >
                          Ausspielen
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>,
                ...fassungen.map((row) => {
                const global = Number(row.site_id) === SNIPPET_GLOBAL_SITE_ID;
                // ⚠ Der Nenner kommt vom SERVER (dieselbe Bedingung wie `zielPortale`), nicht
                // aus `sites.length` — sonst zeigt ein globaler Schnipsel mit Portal-Ueberdeckung
                // dauerhaft `5/6` in Warnfarbe, obwohl alles stimmt.
                const ziele = Number(row.ziel_anzahl);
                const aus = Number(row.enabled) === 0;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={
                            offen === row.id ? 'Details einklappen' : 'Details ausklappen'
                          }
                          onClick={() => umschalten(row)}
                        >
                          {offen === row.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      {/*
                        ⚠ Der NAME steht in der Gruppenzeile darueber, nicht hier. Ihn je Fassung
                        zu wiederholen war die erste Fassung dieses Umbaus — und machte aus einer
                        Namensgruppe eine Wand aus demselben Wort. Was eine Fassung unterscheidet,
                        ist ihre EBENE (naechste Spalte), nicht ihr Name.
                      */}
                      <TableCell>
                        {row.title ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.title}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            ohne internen Titel
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={global ? <PublicOutlined /> : <StorefrontOutlined />}
                          label={global ? 'global' : (row.site_name ?? row.site_key ?? '?')}
                          color={global ? 'default' : 'primary'}
                          variant="outlined"
                        />
                        {row.archived_at && (
                          <Tooltip title="Archiviert: aus der Arbeitsliste geräumt. Nichts wurde gelöscht.">
                            <Chip size="small" label="archiviert" sx={{ ml: 0.5 }} color="default" variant="outlined" />
                          </Tooltip>
                        )}
                        {aus && (
                          <Tooltip title="Die Fassung ist abgeschaltet: sie bleibt erhalten, wirkt aber nicht. Auf den Portalen gilt dann die globale Fassung — oder nichts.">
                            <Chip size="small" label="aus" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.broker_name ?? (
                          <Typography variant="caption" color="text.secondary">
                            kein Bezug
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            Number(row.fehler_auf) > 0
                              ? `${row.fehler_auf} Portal(e) mit hinterlegtem Fehlergrund — aufklappen für den Grund`
                              : 'Portale, deren ausgespielter Stand dem aktuellen entspricht'
                          }
                        >
                          <Chip
                            size="small"
                            icon={
                              Number(row.fehler_auf) > 0 ? (
                                <ErrorOutline />
                              ) : Number(row.aktuell_auf) === ziele ? (
                                <CheckCircleOutline />
                              ) : (
                                <CloudOffOutlined />
                              )
                            }
                            label={`${row.aktuell_auf}/${ziele}`}
                            color={
                              Number(row.fehler_auf) > 0
                                ? 'error'
                                : Number(row.aktuell_auf) === ziele
                                  ? 'success'
                                  : 'warning'
                            }
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          r{row.revision} · {row.updated_by ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Inhalt bearbeiten">
                          <IconButton size="small" onClick={() => setBearbeiten(row.id)}>
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Historie">
                          <IconButton size="small" onClick={() => historieOeffnen(row)}>
                            <HistoryOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {Boolean(row.archived_at) ? (
                          <Tooltip title="Zurück in die Arbeitsliste">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Zurückholen"
                                disabled={aktion === row.id}
                                onClick={() => archivieren(row, true)}
                              >
                                <UnarchiveOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archivieren: aus der Arbeitsliste räumen. Die Fassung und ihre Historie bleiben erhalten — geht erst, wenn sie zurückgenommen ist.">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Archivieren"
                                disabled={aktion === row.id}
                                onClick={() => archivieren(row)}
                              >
                                <ArchiveOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Zurücknehmen: die Fassung abschalten und die Portale auf den dann geltenden Stand bringen">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Zurücknehmen"
                              disabled={aktion === row.id || aus}
                              onClick={() => ausspielen(row, 'takedown')}
                            >
                              <CloudOffOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={offen === row.id} unmountOnExit>
                          <Box py={2} px={1}>
                            {detailBusy && <CircularProgress size={18} />}
                            {!detailBusy && detail && (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Portal</TableCell>
                                    <TableCell>Dort gilt</TableCell>
                                    <TableCell>Ausspielstand dieser Fassung</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detail.ownership.map((o) => {
                                    const st = detail.sites.find(
                                      (s) => Number(s.site_id) === Number(o.site_id),
                                    );
                                    const selbst = Number(o.owner_id) === Number(row.id);
                                    return (
                                      <TableRow key={o.site_id}>
                                        <TableCell>{o.site_key}</TableCell>
                                        <TableCell>
                                          {o.level === null ? (
                                            <Typography variant="caption" color="text.secondary">
                                              nichts — der Name gibt hier nichts aus
                                            </Typography>
                                          ) : (
                                            <Chip
                                              size="small"
                                              variant={selbst ? 'filled' : 'outlined'}
                                              color={selbst ? 'success' : 'warning'}
                                              label={
                                                selbst
                                                  ? `diese Fassung (${o.level})`
                                                  : `eine ANDERE Fassung (${o.level})`
                                              }
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {st?.last_error ? (
                                            <Typography variant="caption" color="error">
                                              {st.last_error}
                                            </Typography>
                                          ) : st?.deployed_sha256 ? (
                                            <Typography variant="caption" color="text.secondary">
                                              {st.deployed_sha256 === row.sha256
                                                ? 'aktuell'
                                                : 'veraltet'}
                                              {st.deployed_at ? ` · ${st.deployed_at}` : ''}
                                            </Typography>
                                          ) : (
                                            <Typography variant="caption" color="text.secondary">
                                              nie ausgespielt
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            )}
                            {!detailBusy &&
                              detail &&
                              detail.ownership.some(
                                (o) => o.owner_id !== null && Number(o.owner_id) !== Number(row.id),
                              ) && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                  Auf mindestens einem Portal gilt eine andere Fassung dieses
                                  Namens. Änderungen hier wirken dort nicht.
                                </Alert>
                              )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
                }),
              ])}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SnippetAusspielenDialog
        slug={ausspielenFuer}
        fassungen={ausspielenFuer ? (gruppiert.get(ausspielenFuer) ?? []) : []}
        onAbbrechen={() => setAusspielenFuer(null)}
        onAusspielen={(ziele) => {
          const liste = gruppiert.get(ausspielenFuer ?? '') ?? [];
          setAusspielenFuer(null);
          // Irgendeine Fassung des Namens genuegt als Einstieg — der Dienst loest ohnehin je
          // Portal neu auf. Bevorzugt eine aktive, damit die Route nicht an `enabled = 0` haengt.
          const einstieg = liste.find((f) => Number(f.enabled) === 1) ?? liste[0];
          if (einstieg) void ausspielen(einstieg, 'deploy', ziele);
        }}
      />

      <SnippetEditorDialog
        snippetId={bearbeiten}
        sites={sites}
        onClose={() => setBearbeiten(null)}
        onChanged={laden}
      />

      <NewSnippetDialog
        open={neuOffen}
        sites={sites}
        brokers={brokers}
        vorhanden={rows}
        onClose={() => setNeuOffen(false)}
        onCreated={(row) => {
          setNeuOffen(false);
          laden();
          // Ein frisch angelegter Schnipsel ist leer — der einzige sinnvolle naechste Schritt ist,
          // Inhalt hineinzusetzen. Dasselbe Verhalten wie beim Anlegen eines Profils.
          setBearbeiten(Number(row?.id) || null);
        }}
      />

      <Dialog open={Boolean(historie)} onClose={() => setHistorie(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Historie — {historie?.row.slug}</DialogTitle>
        <DialogContent>
          {historie?.v.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Noch keine Fassungen aufgezeichnet.
            </Typography>
          )}
          <Table size="small">
            <TableBody>
              {(historie?.v ?? []).map((v) => (
                <TableRow key={v.version}>
                  <TableCell width={60}>r{v.version}</TableCell>
                  <TableCell>{v.change_note ?? '—'}</TableCell>
                  <TableCell>{v.created_by ?? '—'}</TableCell>
                  <TableCell>{v.created_at}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistorie(null)}>Schliessen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SnippetsTab;
